import {
    CreateEnsAlertRequest,
    ENS_TTS_MAX_TEXT_LENGTH,
    EnsAlertType,
    EnsLevelInputDto,
    EnsTtsRequest,
    EnsUserContactListItem,
    EnsVoicePrompt,
} from "../../../store/features/ens/types";

export const ENS_SMS_SENDER_NOT_CONFIGURED_MESSAGE =
    'No SMS sender number is set up. Please contact support to enable SMS escalation.';
export const DEFAULT_VOICE_PROMPT_TEXT =
    'This is an emergency alert. Please listen carefully and confirm receipt when prompted.';

export const createDefaultVoicePromptDraft = (): EnsTtsRequest => ({
    text: DEFAULT_VOICE_PROMPT_TEXT,
    language: 'ENGLISH',
    gender: 'UNSPECIFIED',
});

export const isVoicePromptDraftValid = (draft: EnsTtsRequest): boolean => {
    const text = draft.text.trim();
    return text.length > 0 && draft.text.length <= ENS_TTS_MAX_TEXT_LENGTH;
};

export const normalizeVoicePromptDraft = (draft: EnsTtsRequest): EnsTtsRequest => ({
    ...draft,
    text: draft.text.trim(),
});

export const normalizeEscalationLevels = (levels: readonly EnsLevelInputDto[]): EnsLevelInputDto[] =>
    levels.map((level, index) => ({
        ...level,
        sequenceNo: index + 1,
        delaySecondsFromPrevious: index === 0 ? 0 : level.delaySecondsFromPrevious,
    }));

export const requiresVoicePrompt = (levels: EnsLevelInputDto[]): boolean =>
    levels.some((l) => l.channel === 'PSTN' || l.channel === 'INTERNAL_CALL');

export const hasAlertTargets = (targetGroupIds: readonly number[], targetUserIds: readonly number[]): boolean =>
    targetGroupIds.length + targetUserIds.length > 0;

export const formatTargetSummary = (groupCount: number, individualCount: number): string => {
    const parts: string[] = [];
    if (groupCount > 0) parts.push(`${groupCount} ${groupCount === 1 ? 'group' : 'groups'}`);
    if (individualCount > 0) {
        parts.push(`${individualCount} individual ${individualCount === 1 ? 'recipient' : 'recipients'}`);
    }
    return parts.length > 0 ? parts.join(' + ') : 'No recipients selected';
};

interface CreateAlertDraft {
    type: EnsAlertType;
    name: string;
    message: string;
    targetGroupIds: readonly number[];
    targetUserIds: readonly number[];
    levels: readonly EnsLevelInputDto[];
    voicePrompt: EnsVoicePrompt | null;
}

export const buildCreateAlertRequest = (draft: CreateAlertDraft): CreateEnsAlertRequest => ({
    type: draft.type,
    name: draft.name.trim(),
    message: draft.message.trim(),
    targetGroupIds: [...draft.targetGroupIds],
    targetUserIds: [...draft.targetUserIds],
    levels: normalizeEscalationLevels(draft.levels),
    voicePrompt: draft.voicePrompt,
});

export const formatDuration = (seconds: number): string => {
    const total = Math.max(0, Math.round(seconds));
    if (total < 60) return `${total}s`;
    if (total < 3600) return `${Math.round(total / 60)}m`;
    const hours = Math.floor(total / 3600);
    const minutes = Math.round((total % 3600) / 60);
    return minutes === 0 ? `${hours}h` : `${hours}h ${minutes}m`;
};

const MSISDN_CHANNELS: ReadonlyArray<EnsLevelInputDto['channel']> = ['SMS', 'PSTN', 'WHATSAPP'];

export const levelsRequireExternalMsisdn = (levels: readonly EnsLevelInputDto[]): boolean =>
    levels.some((level) => MSISDN_CHANNELS.includes(level.channel));

export const hasEligibleEnsNumber = (contact: EnsUserContactListItem): boolean =>
    contact.externalMsisdn != null && contact.ensEligible;

export const formatDirectRecipientsWithoutNumber = (
    missingCount: number,
    directRecipientCount: number,
): string => {
    const recipientLabel = directRecipientCount === 1 ? 'recipient' : 'recipients';
    const verb = missingCount === 1 ? 'is' : 'are';
    return `${missingCount} of ${directRecipientCount} directly selected ${recipientLabel} ${verb} missing an eligible emergency phone number.`;
};

export interface UnreachableRecipient {
    userId: number;
    displayName: string;
}

export interface SubmitBlockedError {
    message: string;
    unreachableRecipients: UnreachableRecipient[];
}

export const toSubmitBlockedError = (error: unknown): SubmitBlockedError | null => {
    if (typeof error !== 'object' || error === null) return null;
    const data = (error as {
        data?: {
            code?: string;
            message?: string;
            details?: { unreachableRecipients?: UnreachableRecipient[] };
        };
    }).data;
    if (data?.code !== 'UNPROCESSABLE_ENTITY' || !data.message) return null;
    return {
        message: data.message,
        unreachableRecipients: data.details?.unreachableRecipients ?? [],
    };
};
